import AsyncStorage from '@react-native-async-storage/async-storage'

export const ACTIVE_STORAGE_SCOPE_USER_ID_KEY = 'storage.scope.activeUserId'

const USER_SCOPE_SEGMENT = 'user'

let activeStorageScopeUserIdCache: string | null | undefined

export function resetActiveStorageScopeCache(): void {
  activeStorageScopeUserIdCache = undefined
}

function normalizeStorageScopeUserId(
  userId: string | null | undefined,
): string | null {
  if (typeof userId !== 'string') {
    return null
  }

  const trimmedUserId = userId.trim()
  return trimmedUserId.length > 0 ? trimmedUserId : null
}

function encodeStorageScopeUserId(userId: string): string {
  return encodeURIComponent(userId)
}

export function buildScopedStorageKey(
  key: string,
  userId: string | null | undefined,
): string {
  const normalizedUserId = normalizeStorageScopeUserId(userId)
  if (normalizedUserId == null) {
    return key
  }

  return `${key}::${USER_SCOPE_SEGMENT}::${encodeStorageScopeUserId(normalizedUserId)}`
}

export async function getActiveStorageScopeUserId(): Promise<string | null> {
  if (activeStorageScopeUserIdCache !== undefined) {
    return activeStorageScopeUserIdCache
  }

  try {
    const storedUserId = await AsyncStorage.getItem(
      ACTIVE_STORAGE_SCOPE_USER_ID_KEY,
    )
    activeStorageScopeUserIdCache = normalizeStorageScopeUserId(storedUserId)
  } catch {
    activeStorageScopeUserIdCache = null
  }

  return activeStorageScopeUserIdCache
}

export async function setActiveStorageScopeUserId(
  userId: string | null | undefined,
): Promise<void> {
  const normalizedUserId = normalizeStorageScopeUserId(userId)
  activeStorageScopeUserIdCache = normalizedUserId

  try {
    if (normalizedUserId == null) {
      await AsyncStorage.removeItem(ACTIVE_STORAGE_SCOPE_USER_ID_KEY)
      return
    }

    await AsyncStorage.setItem(
      ACTIVE_STORAGE_SCOPE_USER_ID_KEY,
      normalizedUserId,
    )
  } catch {
    // Best-effort only. The in-memory scope still updates for the current session.
  }
}

export async function getScopedStorageKey(
  key: string,
  options?: {
    requireUserScope?: boolean
    userId?: string | null
  },
): Promise<string | null> {
  const normalizedUserId =
    options?.userId !== undefined
      ? normalizeStorageScopeUserId(options.userId)
      : await getActiveStorageScopeUserId()

  if (normalizedUserId == null) {
    return options?.requireUserScope ? null : key
  }

  return buildScopedStorageKey(key, normalizedUserId)
}
